import { colors } from './colors';

/**
 * Component Theme Configuration
 *
 * This file provides reusable theme styles for common UI components.
 * All styles use the centralized color palette from colors.ts.
 *
 * To customize the theme:
 * 1. Modify colors in colors.ts (primary.background, accent.blue, etc.)
 * 2. The changes will automatically apply to all components using these styles
 *
 * Usage Example:
 * import { dialogTheme, buttonTheme } from '../theme/theme';
 *
 * <div style={dialogTheme.header}>Header Content</div>
 * <button style={buttonTheme.primary}>Save</button>
 */

// ============================================================================
// Dialog Theme
// ============================================================================

export const dialogTheme = {
  /**
   * Dialog Header Styles
   * Used for dialog/modal headers
   */
  header: {
    background: colors.primary.background,
    color: colors.primary.text,
    padding: '1.5rem 2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.neutral.grey[200]}`,
    borderRadius: '0.5rem 0.5rem 0 0',
  } as React.CSSProperties,

  /**
   * Dialog Title Styles
   */
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: colors.primary.text,
    margin: 0,
  } as React.CSSProperties,

  /**
   * Dialog Body Styles
   */
  body: {
    padding: '2rem',
    backgroundColor: colors.neutral.white,
    maxHeight: '70vh',
    overflowY: 'auto' as const,
  } as React.CSSProperties,

  /**
   * Dialog Footer Styles
   */
  footer: {
    padding: '1.5rem 2rem',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    borderTop: `1px solid ${colors.neutral.grey[200]}`,
    backgroundColor: colors.neutral.grey[50],
    borderRadius: '0 0 12px 12px',
  } as React.CSSProperties,

  /**
   * Close Button Styles (X button in header)
   */
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: colors.primary.text,
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,

  closeButtonHover: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  } as React.CSSProperties,
} as const;

// ============================================================================
// Button Theme
// ============================================================================

export const buttonTheme = {
  /**
   * Base button styles (shared by all buttons)
   */
  base: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  } as React.CSSProperties,

  /**
   * Primary Button (main actions like Save, Submit)
   */
  primary: {
    backgroundColor: colors.accent.blue,
    color: colors.neutral.white,
  } as React.CSSProperties,

  primaryHover: {
    backgroundColor: colors.accent.blueHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
  } as React.CSSProperties,

  primaryActive: {
    backgroundColor: colors.accent.blueDark,
    transform: 'translateY(0)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  } as React.CSSProperties,

  primaryDisabled: {
    backgroundColor: colors.neutral.grey[400],
    cursor: 'not-allowed',
    opacity: 0.6,
  } as React.CSSProperties,

  /**
   * Secondary Button (less important actions like Cancel)
   */
  secondary: {
    backgroundColor: colors.neutral.white,
    color: colors.neutral.grey[700],
    border: `2px solid ${colors.neutral.grey[300]}`,
  } as React.CSSProperties,

  secondaryHover: {
    backgroundColor: colors.neutral.grey[50],
    borderColor: colors.neutral.grey[400],
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  } as React.CSSProperties,

  secondaryActive: {
    backgroundColor: colors.neutral.grey[100],
    transform: 'translateY(0)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  } as React.CSSProperties,

  /**
   * Success Button (positive actions like Approve, Confirm)
   */
  success: {
    backgroundColor: colors.accent.green,
    color: colors.neutral.white,
  } as React.CSSProperties,

  successHover: {
    backgroundColor: colors.accent.greenHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
  } as React.CSSProperties,

  /**
   * Warning Button (cautionary actions)
   */
  warning: {
    backgroundColor: colors.accent.yellow,
    color: colors.neutral.white,
  } as React.CSSProperties,

  warningHover: {
    backgroundColor: colors.accent.yellowHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
  } as React.CSSProperties,

  /**
   * Danger Button (destructive actions like Delete)
   */
  danger: {
    backgroundColor: colors.accent.red,
    color: colors.neutral.white,
  } as React.CSSProperties,

  dangerHover: {
    backgroundColor: colors.accent.redHover,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
  } as React.CSSProperties,

  /**
   * Ghost Button (minimal styling)
   */
  ghost: {
    backgroundColor: 'transparent',
    color: colors.neutral.grey[700],
    boxShadow: 'none',
  } as React.CSSProperties,

  ghostHover: {
    backgroundColor: colors.neutral.grey[100],
  } as React.CSSProperties,
} as const;

// ============================================================================
// Form Theme
// ============================================================================

export const formTheme = {
  /**
   * Form Group (label + input container)
   */
  group: {
    marginBottom: '1.5rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  } as React.CSSProperties,

  /**
   * Form Label
   */
  label: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: colors.neutral.grey[700],
    marginBottom: '0.5rem',
  } as React.CSSProperties,

  /**
   * Form Input (text, number, etc.)
   */
  input: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    border: `2px solid ${colors.neutral.grey[300]}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: colors.neutral.white,
    color: colors.neutral.grey[900],
  } as React.CSSProperties,

  inputFocus: {
    borderColor: colors.accent.blue,
    boxShadow: `0 0 0 3px ${colors.accent.blue}20`,
  } as React.CSSProperties,

  inputError: {
    borderColor: colors.accent.red,
    boxShadow: `0 0 0 3px ${colors.accent.red}20`,
  } as React.CSSProperties,

  /**
   * Select Dropdown
   */
  select: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    border: `2px solid ${colors.neutral.grey[300]}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: colors.neutral.white,
    color: colors.neutral.grey[900],
    cursor: 'pointer',
  } as React.CSSProperties,

  /**
   * Textarea
   */
  textarea: {
    padding: '0.75rem 1rem',
    fontSize: '0.875rem',
    border: `2px solid ${colors.neutral.grey[300]}`,
    borderRadius: '8px',
    outline: 'none',
    transition: 'all 0.2s ease',
    backgroundColor: colors.neutral.white,
    color: colors.neutral.grey[900],
    resize: 'vertical' as const,
    minHeight: '100px',
  } as React.CSSProperties,

  /**
   * Error Message
   */
  error: {
    fontSize: '0.75rem',
    color: colors.accent.red,
    marginTop: '0.25rem',
  } as React.CSSProperties,

  /**
   * Helper Text
   */
  helper: {
    fontSize: '0.75rem',
    color: colors.neutral.grey[500],
    marginTop: '0.25rem',
  } as React.CSSProperties,
} as const;

// ============================================================================
// Card Theme
// ============================================================================

export const cardTheme = {
  /**
   * Card Container
   */
  container: {
    backgroundColor: colors.neutral.white,
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
  } as React.CSSProperties,

  /**
   * Card Header
   */
  header: {
    padding: '1.5rem',
    backgroundColor: colors.primary.background,
    color: colors.primary.text,
    borderBottom: `1px solid ${colors.neutral.grey[200]}`,
  } as React.CSSProperties,

  /**
   * Card Body
   */
  body: {
    padding: '1.5rem',
  } as React.CSSProperties,

  /**
   * Card Footer
   */
  footer: {
    padding: '1rem 1.5rem',
    backgroundColor: colors.neutral.grey[50],
    borderTop: `1px solid ${colors.neutral.grey[200]}`,
  } as React.CSSProperties,
} as const;

// ============================================================================
// Table Theme (for non-AG Grid tables)
// ============================================================================

export const tableTheme = {
  /**
   * Table Container
   */
  container: {
    width: '100%',
    overflowX: 'auto' as const,
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  /**
   * Table
   */
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  } as React.CSSProperties,

  /**
   * Table Header
   */
  thead: {
    backgroundColor: colors.primary.background,
    color: colors.primary.text,
  } as React.CSSProperties,

  /**
   * Table Header Cell
   */
  th: {
    padding: '1rem',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: '0.875rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    borderBottom: `2px solid ${colors.neutral.grey[300]}`,
  } as React.CSSProperties,

  /**
   * Table Body
   */
  tbody: {
    backgroundColor: colors.neutral.white,
  } as React.CSSProperties,

  /**
   * Table Row
   */
  tr: {
    borderBottom: `1px solid ${colors.neutral.grey[100]}`,
    transition: 'background-color 0.2s ease',
  } as React.CSSProperties,

  trHover: {
    backgroundColor: colors.neutral.grey[50],
  } as React.CSSProperties,

  /**
   * Table Cell
   */
  td: {
    padding: '1rem',
    fontSize: '0.875rem',
    color: colors.neutral.grey[700],
  } as React.CSSProperties,
} as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Merges multiple style objects
 * Useful for combining base styles with conditional styles
 */
export const mergeStyles = (...styles: React.CSSProperties[]): React.CSSProperties => {
  return Object.assign({}, ...styles);
};

/**
 * Creates a hover handler that applies hover styles
 *
 * Usage:
 * const { onMouseEnter, onMouseLeave } = createHoverHandler(baseStyle, hoverStyle);
 * <button onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>Hover Me</button>
 */
export const createHoverHandler = (
  baseStyle: React.CSSProperties,
  hoverStyle: React.CSSProperties
) => {
  return {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      Object.assign(e.currentTarget.style, hoverStyle);
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      Object.assign(e.currentTarget.style, baseStyle);
    },
  };
};

/**
 * Gets button styles based on variant and state
 */
export const getButtonStyles = (
  variant: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'ghost' = 'primary',
  disabled = false
): React.CSSProperties => {
  const baseStyle = buttonTheme.base;
  const variantStyle = buttonTheme[variant];

  if (disabled && variant === 'primary') {
    return mergeStyles(baseStyle, variantStyle, buttonTheme.primaryDisabled);
  }

  return mergeStyles(baseStyle, variantStyle);
};

// ============================================================================
// Export All
// ============================================================================

export default {
  dialog: dialogTheme,
  button: buttonTheme,
  form: formTheme,
  card: cardTheme,
  table: tableTheme,
  colors,
};
